// src/resources/rides.js
import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    ReferenceField,
    NumberField,
    ChipField,
    Show,
    SimpleShowLayout,
    ReferenceManyField,
    ShowButton,
    useRecordContext,
    TopToolbar,
    Title,
    FunctionField
} from 'react-admin';
import { 
    Box, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Divider 
} from '@mui/material'; 
import { BanUserButton } from './users'; 
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import RouteIcon from '@mui/icons-material/Route';

// --- قائمة عرض الرحلات (Ride List) - كما هي ---
export const RideList = (props) => {
  return (
    <List {...props} title="All Rides" sort={{ field: 'createdAt', order: 'DESC' }}>
      <Datagrid rowClick="show">
        <TextField source="origin" label="From" />
        <TextField source="destination" label="To" />
        <TextField source="driver_name" label="Driver" />
        <DateField source="time" showTime />
        <NumberField source="price" options={{ style: 'currency', currency: 'EGP' }} />
        <TextField source="seats" />
        <ChipField source="status" />
        <ShowButton />
      </Datagrid>
    </List>
  );
};

// --- عنوان الصفحة ---
const RideShowTitle = () => {
    const record = useRecordContext();
    return <Title title={record ? `Ride Details #${record.id}` : 'Ride Details'} />;
};

// --- شريط الأدوات (زر الحظر) ---
const RideShowActions = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    return (
        <TopToolbar>
            <BanUserButton userId={record.driverId} />
        </TopToolbar>
    );
};

// --- تنسيق الحقل (Label + Value) ---
const FieldWrapper = ({ label, children }) => (
    <Box mb={2}>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
            {label}
        </Typography>
        <Typography variant="body1" component="div">
            {children}
        </Typography>
    </Box>
);

// --- صفحة عرض التفاصيل (Ride Show) - التصميم الجديد ---
export const RideShow = (props) => {
    return (
        <Show {...props} title={<RideShowTitle />} actions={<RideShowActions />}>
            <SimpleShowLayout sx={{ backgroundColor: 'transparent', padding: 0 }}>
                
                {/* الصف الأول: معلومات المسار والسائق */}
                <Grid container spacing={2} sx={{ width: '100%' }}>
                    
                    {/* 1. بطاقة المسار والوقت */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <RouteIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Route & Time</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FieldWrapper label="From (Origin)">
                                            <TextField source="origin" />
                                        </FieldWrapper>
                                        <FieldWrapper label="City">
                                            <TextField source="fromCity" />
                                        </FieldWrapper>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FieldWrapper label="To (Destination)">
                                            <TextField source="destination" />
                                        </FieldWrapper>
                                        <FieldWrapper label="City">
                                            <TextField source="toCity" />
                                        </FieldWrapper>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 1 }} />
                                <FieldWrapper label="Departure Time">
                                    <DateField source="time" showTime options={{ weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }} />
                                </FieldWrapper>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 2. بطاقة السائق والسيارة */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <PersonIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Driver & Car</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <FieldWrapper label="Driver Name">
                                    <ReferenceField source="driverId" reference="users" link="show">
                                        <TextField source="name" style={{ fontWeight: 'bold', fontSize: '1.1rem' }} />
                                    </ReferenceField>
                                </FieldWrapper>

                                <Box mt={3} display="flex" alignItems="center" mb={1}>
                                    <DirectionsCarIcon color="action" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1">Car Details</Typography>
                                </Box>
                                
                                <ReferenceField source="carId" reference="cars" link={false} allowEmpty>
                                    <FunctionField render={record => 
                                        record ? (
                                            <Box bgcolor="grey.100" p={1} borderRadius={1}>
                                                <Typography variant="body2"><strong>Plate:</strong> {record.plate}</Typography>
                                                <Typography variant="body2"><strong>Model:</strong> {record.brand} {record.model} ({record.year})</Typography>
                                                <Typography variant="body2"><strong>Color:</strong> {record.color}</Typography>
                                            </Box>
                                        ) : 'No Car Assigned'
                                    } />
                                </ReferenceField>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 3. بطاقة تفاصيل الرحلة (السعر - الحالة - معلومات إضافية) */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <InfoIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Ride Information</Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={3}>
                                    <Grid item xs={6} md={3}>
                                        <FieldWrapper label="Price per Seat">
                                            <NumberField source="price" options={{ style: 'currency', currency: 'EGP' }} style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'green' }} />
                                        </FieldWrapper>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FieldWrapper label="Total Seats">
                                            <TextField source="seats" />
                                        </FieldWrapper>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FieldWrapper label="Current Status">
                                            <ChipField source="status" />
                                        </FieldWrapper>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FieldWrapper label="Ride Type">
                                            <ChipField source="rideType" />
                                        </FieldWrapper>
                                    </Grid>
                                </Grid>

                                {/* قسم المعلومات الإضافية يظهر فقط لو موجود */}
                                <Box mt={2}>
                                    <FieldWrapper label="Additional Info / Notes">
                                        <TextField source="additionalInfo" emptyText="No additional information provided." />
                                    </FieldWrapper>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 4. قائمة الركاب */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Passengers List</Typography>
                                <Divider sx={{ mb: 2 }} />
                                
                                <ReferenceManyField label={false} reference="bookings" target="rideId">
                                    <Datagrid bulkActionButtons={false} sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}>
                                        <ReferenceField source="userId" reference="users" label="Passenger Name" link="show">
                                            <TextField source="name" />
                                        </ReferenceField>
                                        <ReferenceField source="userId" reference="users" label="Phone" link={false}>
                                            <TextField source="phone" />
                                        </ReferenceField>
                                        <ChipField source="status" label="Booking Status" />
                                        <NumberField source="seatsBooked" label="Seats" />
                                        <DateField source="createdAt" label="Booked At" showTime />
                                    </Datagrid>
                                </ReferenceManyField>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
            </SimpleShowLayout>
        </Show>
    );
};